import { Button, Group, Popover, Stack } from '@mantine/core';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { type MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MATH_KEYBOARD_LIFT_PROPS, MathLiveField } from 'shared/components/MathLiveField';

import { renderStaticMathDisplayHtml } from '../../math/staticMathHtml';

/**
 * React node view for the `inlineMath` node. Renders the formula with KaTeX and, when
 * editable, opens a mathlive popover on DOUBLE-click (or Enter/Space when focused) so the
 * formula can be edited live and written back to the node's `latex` attribute.
 *
 * Single-click / drag are intentionally left to ProseMirror: the node is an `atom` +
 * `selectable`, so a plain click selects it and it participates in range selections — exactly
 * like normal content. (Previously a `mousedown`-`preventDefault` + click-to-edit suppressed
 * all of that, so the formula couldn't be selected at all.) `selected` drives the selection ring.
 */
export function InlineMathNodeView({ editor, getPos, node, selected, updateAttributes }: NodeViewProps) {
  const { t } = useTranslation('widgets.mathControl');
  const latex = (node.attrs.latex as string) ?? '';
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState(latex);

  const html = useMemo(() => renderStaticMathDisplayHtml(latex), [latex]);

  const openEditor = () => {
    if (!editor.isEditable) return;
    setDraft(latex);
    setOpened(true);
  };

  // Single-click selects the formula. We can't just let the click fall through to ProseMirror:
  // the rendered span is focusable (tabIndex, for keyboard a11y), so a plain click would move DOM
  // focus to the span, leaving the editor's contentEditable root unfocused — the node would show
  // as selected but Backspace/Delete would go to the span and do nothing. So we keep focus in the
  // editor and set the NodeSelection ourselves, so the selected formula can be deleted normally.
  const selectSelf = (event: MouseEvent) => {
    // Only a plain primary-button click selects the node. Modified clicks (Shift to extend a range,
    // Ctrl/Cmd/Alt) fall through to ProseMirror's native selection handling untouched.
    if (!editor.isEditable || event.button !== 0 || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey)
      return;
    event.preventDefault();
    const pos = getPos();
    if (typeof pos === 'number') editor.chain().focus().setNodeSelection(pos).run();
  };

  const save = () => {
    updateAttributes({ latex: draft.trim() });
    setOpened(false);
  };

  return (
    // Math is intrinsically LTR; isolate it so it isn't reordered inside RTL text.
    <NodeViewWrapper as="span" style={{ display: 'inline-block', direction: 'ltr', unicodeBidi: 'isolate' }}>
      <Popover
        opened={opened}
        onClose={() => setOpened(false)}
        position="bottom"
        width={360}
        closeOnClickOutside={false}
        trapFocus={false}
      >
        <Popover.Target>
          <span
            // `dir="ltr"` forces LTR base direction (UA maps it to direction:ltr +
            // unicode-bidi:isolate) so KaTeX isn't reordered inside RTL (Hebrew) text.
            dir="ltr"
            contentEditable={false}
            role="button"
            // Keyboard-focusable when editable so keyboard-only authors can open the editor
            // (Enter/Space), not just via double-click.
            tabIndex={editor.isEditable ? 0 : -1}
            aria-label={editor.isEditable ? t('edit') : undefined}
            // Light hover hint; the gesture is double-click (single-click selects).
            title={editor.isEditable ? t('edit') : undefined}
            style={{
              cursor: editor.isEditable ? 'pointer' : 'default',
              display: 'inline-block',
              direction: 'ltr',
              unicodeBidi: 'isolate',
              borderRadius: 4,
              // Selection ring when ProseMirror has this node selected, so it reads as
              // selectable content. Single-click selects via `selectSelf` (keeping editor focus so
              // the node stays deletable); drag from surrounding text extends a range over it
              // natively. Editing is double-click / Enter / Space.
              outline: selected ? '2px solid var(--mantine-color-blue-5)' : undefined,
              backgroundColor: selected ? 'var(--mantine-color-blue-light)' : undefined
            }}
            onMouseDown={selectSelf}
            onDoubleClick={openEditor}
            onKeyDown={event => {
              if (editor.isEditable && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                openEditor();
              }
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Popover.Target>
        {/* Lift marker: the dropdown is viewport-positioned, so when the math keyboard would cover
            it, the whole dropdown is translated above the keyboard (see virtualKeyboardLift.ts). */}
        <Popover.Dropdown {...MATH_KEYBOARD_LIFT_PROPS}>
          <Stack gap="sm">
            <MathLiveField value={draft} onChange={setDraft} autoFocus />
            {/*
              Act on mousedown (with preventDefault), not click: clicking a button blurs the math
              field, MathLive auto-hides the on-screen keyboard, the floating popover reflows, and the
              button slides out from under the cursor so the `click` never fires. preventDefault also
              keeps focus on the field. onClick stays as the keyboard-activation (Enter/Space)
              fallback; save()/close are idempotent so a stray double-fire is harmless.
            */}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                size="xs"
                onMouseDown={(event: MouseEvent) => {
                  event.preventDefault();
                  setOpened(false);
                }}
                onClick={() => setOpened(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                size="xs"
                onMouseDown={(event: MouseEvent) => {
                  event.preventDefault();
                  save();
                }}
                onClick={save}
                disabled={!draft.trim()}
              >
                {t('update')}
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </NodeViewWrapper>
  );
}
